import {
  addDays,
  differenceInCalendarDays,
  differenceInMinutes,
  format,
  getISOWeek,
  parseISO,
} from 'date-fns'

import { CalendarGridMode } from '../../constants/calendarGrid'
import { EMPTY_LIST } from '../../constants/common'
import {
  DATE_API_DATA_FORMAT_SHORT,
  DATE_HOURS_FORMAT,
  DATE_WEEKDAY_SHORT_FORMAT,
  DAY_FIRST_HOUR,
} from '../../constants/date'
import { createDate } from '../../utils/date'
import { formatDate } from '../Table'

import {
  CALENDAR_GRID_DROP_AREA_PREFIX,
  CALENDAR_GRID_MINUTES_PER_ROW,
  CALENDAR_GRID_RANGE_SEPARATOR,
  CALENDAR_GRID_TIME_SEPARATOR,
} from './CalendarGrid.constants'

function formatCalendarGridDate({ dateFormat, locale, timezone, value }) {
  return formatDate({
    date: value,
    dateFormat,
    locale,
    timezone,
    useTodayFormat: false,
  })
}

function getCalendarGridLocalTime({ locale, timezone, value }) {
  return formatCalendarGridDate({
    dateFormat: DATE_HOURS_FORMAT,
    locale,
    timezone,
    value,
  })
    .split(CALENDAR_GRID_TIME_SEPARATOR)
    .map(Number)
}

function getCalendarGridTimeParts({ end, locale, start, timezone }) {
  return (end ? [start, end] : [start]).map(value =>
    formatCalendarGridDate({
      dateFormat: DATE_HOURS_FORMAT,
      locale,
      timezone,
      value,
    })
  )
}

function getCalendarGridEntryItems(item) {
  return item.aggregatedItems ?? [item]
}

export function getCalendarGridItemDay({ item, locale, timezone }) {
  return item.isAllDay
    ? item.date
    : formatCalendarGridDate({
        dateFormat: DATE_API_DATA_FORMAT_SHORT,
        locale,
        timezone,
        value: item.start,
      })
}

export function getCalendarGridTimedItems(items) {
  return items.filter(item => !item.isAllDay)
}

export function getCalendarGridAllDayItems(items) {
  return items.filter(item => item.isAllDay)
}

export function getIsCalendarGridMonthMode(mode) {
  return mode === CalendarGridMode.month
}

export function getIsCalendarGridAgendaMode(mode) {
  return mode === CalendarGridMode.agenda
}

export function getIsCalendarGridToday({ day, today }) {
  return day.date === today
}

export function getCalendarGridWeekNumber(day) {
  return getISOWeek(parseISO(day.date))
}

export function formatCalendarGridDay({ dateFormat, day, locale }) {
  return format(parseISO(day), dateFormat, { locale })
}

export function getCalendarGridTimeRangeLabel({
  end,
  locale,
  start,
  timezone,
}) {
  return getCalendarGridTimeParts({ end, locale, start, timezone }).join(
    CALENDAR_GRID_RANGE_SEPARATOR
  )
}

export function getCalendarGridItemTimeParts({ item, locale, t, timezone }) {
  return item.isAllDay
    ? [t('Calendar.AllDayKey')]
    : getCalendarGridTimeParts({
        end: item.end,
        locale,
        start: item.start,
        timezone,
      })
}

export function getCalendarGridItemTimeLabel({ item, locale, t, timezone }) {
  return getCalendarGridItemTimeParts({ item, locale, t, timezone }).join(
    CALENDAR_GRID_RANGE_SEPARATOR
  )
}

export function getCalendarGridLocalHour({ locale, timezone, value }) {
  const [hour] = getCalendarGridLocalTime({ locale, timezone, value })

  return hour
}

export function getCalendarGridMinuteOffset({
  firstHour,
  locale,
  timezone,
  value,
}) {
  const [hours, minutes] = getCalendarGridLocalTime({
    locale,
    timezone,
    value,
  })

  return (hours - firstHour) * CALENDAR_GRID_MINUTES_PER_ROW + minutes
}

export function getCalendarGridDayTime({ day, minutes, timezone }) {
  const date = parseISO(day)

  return createDate(
    timezone,
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    DAY_FIRST_HOUR,
    minutes
  )
}

export function getCalendarGridItemDuration(item) {
  return item.end
    ? differenceInMinutes(parseISO(item.end), parseISO(item.start))
    : CALENDAR_GRID_MINUTES_PER_ROW
}

export function getIsCalendarGridMultiDayItem(item) {
  return Boolean(item.isAllDay && item.lastDate && item.lastDate > item.date)
}

export function getCalendarGridItemDays({ item, locale, timezone }) {
  const firstDay = getCalendarGridItemDay({ item, locale, timezone })

  return getIsCalendarGridMultiDayItem(item)
    ? Array.from(
        {
          length:
            differenceInCalendarDays(
              parseISO(item.lastDate),
              parseISO(firstDay)
            ) + 1,
        },
        (_, index) =>
          format(addDays(parseISO(firstDay), index), DATE_API_DATA_FORMAT_SHORT)
      )
    : [firstDay]
}

export function getCalendarGridItemCardDayLabel({ item, locale, timezone }) {
  const days = getCalendarGridItemDays({ item, locale, timezone })

  return formatCalendarGridDay({
    dateFormat: DATE_WEEKDAY_SHORT_FORMAT,
    day: days[days.length - 1],
    locale,
  })
}

export function getCalendarGridItemsByDay({ items, locale, timezone }) {
  return items.reduce((acc, item) => {
    getCalendarGridItemDays({ item, locale, timezone }).forEach(day => {
      acc[day] = [...(acc[day] ?? EMPTY_LIST), item]
    })

    return acc
  }, {})
}

export function getCalendarGridItemsSortedByStart(items) {
  return [...items].sort((left, right) => left.start.localeCompare(right.start))
}

export function getCalendarGridExpandedItems(items) {
  return items.flatMap(getCalendarGridEntryItems)
}

export function getCalendarGridItemsInDayOrder(items) {
  return [
    ...getCalendarGridAllDayItems(items),
    ...getCalendarGridItemsSortedByStart(getCalendarGridTimedItems(items)),
  ]
}

export function getCalendarGridPopoverTitle(item) {
  return item.popover?.title ?? item.title
}

export function getCalendarGridPopoverSecondaryLabel(item) {
  return item.popover && 'secondaryLabel' in item.popover
    ? item.popover.secondaryLabel
    : item.secondaryLabel
}

export function getIsCalendarGridSecondaryLabelClickable({
  item,
  onClickSecondaryLabel,
}) {
  return Boolean(onClickSecondaryLabel && item.isSecondaryLabelClickable)
}

export function getIsCalendarGridPinnedSecondaryLabelClickable({
  item,
  onClickSecondaryLabel,
}) {
  return (
    Boolean(item.isSecondaryLabelPinned) &&
    getIsCalendarGridSecondaryLabelClickable({ item, onClickSecondaryLabel })
  )
}

export function getIsCalendarGridDayFull(dayLoad) {
  return Boolean(dayLoad?.isFull)
}

export function getCalendarGridDropAreaId(day) {
  return `${CALENDAR_GRID_DROP_AREA_PREFIX}${day}`
}
